// components/SummaryViewCard.js
// Entry-point card for the Summary View feature — used identically on both
// clinician-facing surfaces (Clinician.js and TelehealthReviewDashboard.js)
// so the feature reads as its own distinct functionality, reachable via its
// own dedicated page, rather than appearing inline on top of another page.
import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";

const B = { navy: "#0a0e27", navyMid: "#1a2244", navyDark: "#050714", accent: "#637cdb" };

const FEATURES = [
  { icon: <PersonSearchRoundedIcon sx={{ fontSize: 15 }} />, text: "Search any linked patient" },
  { icon: <InsightsRoundedIcon sx={{ fontSize: 15 }} />, text: "Every report type, one view" },
  { icon: <PictureAsPdfRoundedIcon sx={{ fontSize: 15 }} />, text: "Download as a branded PDF" },
];

export default function SummaryViewCard({ to }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const bg = isDark ? "rgba(17,24,39,0.9)" : "rgba(255,255,255,0.92)";
  const textSub = isDark ? "#94a3b8" : "#475569";

  return (
    <motion.div
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={() => navigate(to)}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      style={{ width: "100%", cursor: "pointer" }}
    >
      <Box sx={{
        display: "flex", flexDirection: { xs: "column", md: "row" },
        borderRadius: "20px", overflow: "hidden", position: "relative",
        background: isDark
          ? "linear-gradient(rgba(17,24,39,0.9), rgba(17,24,39,0.9)) padding-box, linear-gradient(135deg, rgba(10,14,39,0.6), rgba(99,124,219,0.4)) border-box"
          : "linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)) padding-box, linear-gradient(135deg, rgba(10,14,39,0.25), rgba(99,124,219,0.35)) border-box",
        border: "1.5px solid transparent",
        backdropFilter: "blur(16px) saturate(140%)",
        WebkitBackdropFilter: "blur(16px) saturate(140%)",
        boxShadow: hovered
          ? isDark
            ? "0 28px 72px rgba(0,0,0,0.5), 0 0 0 1px rgba(99,124,219,0.25), 0 0 50px -18px rgba(99,124,219,0.5)"
            : "0 28px 72px rgba(10,14,39,0.18), 0 0 0 1px rgba(99,124,219,0.3), 0 0 40px -18px rgba(99,124,219,0.35)"
          : isDark
            ? "0 4px 24px rgba(0,0,0,0.4)"
            : "0 4px 24px rgba(10,14,39,0.08)",
        transition: "box-shadow 0.3s ease",
      }}>
        <Box sx={{
          px: 3.5, py: 3, position: "relative", overflow: "hidden", flexShrink: 0,
          width: { xs: "100%", md: 260 },
          display: "flex", flexDirection: "column", justifyContent: "center",
          background: `linear-gradient(135deg, ${B.navy} 0%, ${B.navyMid} 100%)`,
        }}>
          <Box sx={{ position: "absolute", top: -50, right: -50, width: 160, height: 160,
            borderRadius: "50%", border: "1px solid rgba(99,124,219,0.14)" }} />

          <motion.div animate={{ scale: hovered ? 1.06 : 1, rotate: hovered ? -6 : 0 }} transition={{ duration: 0.3 }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: "14px", mb: 1.5,
              display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: "rgba(99,124,219,0.18)", border: "1px solid rgba(99,124,219,0.32)",
            }}>
              <AutoAwesomeRoundedIcon sx={{ color: "#a8b6f0", fontSize: 26 }} />
            </Box>
          </motion.div>

          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: 0.75,
            bgcolor: "rgba(99,124,219,0.15)", border: "1px solid rgba(99,124,219,0.25)",
            borderRadius: "99px", px: 1.5, py: 0.4, mb: 0.75, alignSelf: "flex-start",
          }}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: B.accent, letterSpacing: "0.15em" }}>
              CLINICIAN TOOLS
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1.25 }}>
            Summary View
          </Typography>
        </Box>

        <Box sx={{
          px: 3.5, py: 3, flex: 1, minWidth: 0, bgcolor: bg,
          display: "flex", flexDirection: "column", justifyContent: "center", gap: 2,
        }}>
          <Typography sx={{ fontSize: 13.5, color: textSub, lineHeight: 1.75 }}>
            Pick a patient and see every report type they have — Opioid Risk, Pain Perception,
            Drug Metabolism — rendered as one visual snapshot, or download it as a branded PDF.
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {FEATURES.map((f, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center", gap: 1,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.03)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.06)"}`,
                borderRadius: "10px", px: 1.25, py: 0.75,
              }}>
                <Box sx={{ color: B.accent, display: "flex" }}>{f.icon}</Box>
                <Typography sx={{ fontSize: 12.5, color: isDark ? "#cbd5e1" : "#334155", fontWeight: 500, whiteSpace: "nowrap" }}>
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box>
            <Button
              variant="contained"
              endIcon={
                <motion.div animate={{ x: hovered ? 4 : 0 }} transition={{ duration: 0.2 }}>
                  <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                </motion.div>
              }
              sx={{
                bgcolor: B.navy, color: "#fff", fontWeight: 700, fontSize: 13.5,
                py: 1.2, px: 3, borderRadius: "10px", textTransform: "none",
                boxShadow: hovered ? "0 8px 24px rgba(10,14,39,0.4)" : "0 2px 8px rgba(10,14,39,0.2)",
                transition: "all 0.25s ease",
                background: hovered ? `linear-gradient(135deg, ${B.navyDark}, ${B.navyMid})` : B.navy,
              }}
            >
              Open Summary View
            </Button>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}
